import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskPersonalAccountQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/task/helpdesk-task-personal-account-query.dto';
import { HdPrefixService } from '@zaoblako/web/core/services/prefix/hd-prefix.service';
import { PersonalAccountHdTaskService } from '@zaoblako/web/core/services/lk/personal-account-hd-task.service';
import { NoDataSize } from '@zaoblako/web/shared/components/no-data/constants/no-data-types.enum';
import { NoDataComponent } from '@zaoblako/web/shared/components/no-data/no-data.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ConcatDateParamsPipe } from '@zaoblako/web/shared/pipes/concat-date-params.pipe';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';

@Component({
  standalone: true,
  selector: 'crm-lk-eu-notification-favorite-list',
  templateUrl: './lk-eu-notification-favorite-list.component.html',
  styleUrls: ['./lk-eu-notification-favorite-list.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    ProgressSpinnerModule,
    TooltipModule,
    NoDataComponent,
    SpinnerComponent,
    ConcatDateParamsPipe,
    TruncatePipe,
    CardModule,
    DividerModule,
    SafeHtmlPipe,
    StatusLabelComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuNotificationFavoriteListComponent implements OnInit {
  @Output() public closeSidebar = new EventEmitter<void>();

  public tasks: HelpdeskTaskFindInterface[] = [];
  public loading = false;
  public paginationLoading = false;
  public count: number;

  public queryParams: HelpdeskTaskPersonalAccountQueryInterface = {
    limit: 20,
    offset: 0,
    onlyFavorites: true
  };

  public NoDataSize: NoDataSize.M;

  public constructor(
    private readonly personalAccountHdTaskService: PersonalAccountHdTaskService,
    public readonly hdPrefixService: HdPrefixService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.loadNotifications();
  }

  public loadNotifications({ concat } = { concat: false }) {
    if (!concat) {
      this.loading = true;
      this.cdRef.markForCheck();
    }

    this.personalAccountHdTaskService.findWithPagination(this.queryParams).subscribe({
      next: (tasks) => {
        this.tasks = concat ? this.tasks.concat(tasks.items) : tasks.items;
        this.count = tasks.count;
        this.paginationLoading = false;
        this.loading = false;
        this.cdRef.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public onScrollEnd(target: HTMLElement) {
    if (target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight) {
      if (this.tasks.length < this.count && !this.loading && !this.paginationLoading) {
        this.paginationLoading = true;
        this.queryParams.offset += this.queryParams.limit;
        this.loadNotifications({ concat: true });
      }
    }
  }

  public toTask(task: any): HelpdeskTaskFindInterface {
    return task as HelpdeskTaskFindInterface;
  }
}
